// Upload step of the questionnaire (optional).
//
// Since 2026-10-16 (Paul) it works like the upload link: the patient says what each document is,
// the file uploads as soon as it is picked (with progress) and can be removed while it waits, and
// "Weiter" sends everything to the practice — one PDF per document type, named with the patient
// number, in the right PsyCloud category. "Weiter" never blocks on a failed send: the files are
// stored, and the server sends what nobody sent after 24 h.
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UploadCloud, FileText, CheckCircle2, AlertTriangle, Loader2, Camera, RotateCw, X, Clock, Trash2 } from 'lucide-react'
import { Button } from '../components/Button'
import { FormNavigation } from '../components/FormNavigation'
import { documentService, getApiErrorCode, getApiErrorStatus } from '../services/api'
import { reportUploadError } from '../services/errorReporter'
import { MAX_UPLOAD_BYTES, formatFileSize } from '../utils/fileLimits'
import { useOnboardingStore, type StepUpload } from '../store/onboardingStore'
import { useTestMode } from '../context/TestModeContext'
import { CHOOSABLE_DOCUMENT_TYPES, isDocumentType } from '../data/documentTypes'
import { cn } from '../utils/cn'
import { toast } from 'sonner'
import { logger } from '../utils/logger'
import type { StepProps } from '../types'

type ErrorKey = 'tooLarge' | 'unsupported' | 'heic' | 'conversion' | 'imageTooLarge' | 'failed' | 'rateLimited'

/** A file of this visit that is not stored yet: uploading, refused, or (test mode) kept locally */
interface Item {
  key: string
  name: string
  size: number
  documentType: string
  status: 'queued' | 'uploading' | 'error' | 'local'
  progress: number
  error?: ErrorKey
  /** kept for a retry */
  file?: File
}

const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
const ALLOWED_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png'])
let nextKey = 0

function uploadError(err: unknown): ErrorKey {
  const code = getApiErrorCode(err)
  if (code === 'file_too_large' || getApiErrorStatus(err) === 413) return 'tooLarge'
  if (code === 'unsupported_image') return 'heic'
  if (code === 'unsupported_file') return 'unsupported'
  if (code === 'image_conversion_failed') return 'conversion'
  if (code === 'image_too_large') return 'imageTooLarge'
  if (getApiErrorStatus(err) === 429 && code !== 'upload_limit_reached') return 'rateLimited'
  return 'failed'
}

export function Step4DocumentUpload({ onNext, onBack }: StepProps) {
  const { t, i18n } = useTranslation()
  const isTestMode = useTestMode()
  const { patientNumber, personalData, stepUploads, setStepUploads, uploadedDocuments, setUploadedDocuments } = useOnboardingStore()
  const [items, setItems] = useState<Item[]>([])
  const [docType, setDocType] = useState('')
  const [dragging, setDragging] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const cameraInput = useRef<HTMLInputElement>(null)
  // one upload at a time, in the order the patient chose the files
  const queue = useRef<Promise<void>>(Promise.resolve())

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  const lang = i18n.language

  const patch = (key: string, change: Partial<Item>) => setItems(prev => prev.map(i => (i.key === key ? { ...i, ...change } : i)))

  const upload = useCallback((item: Item) => {
    queue.current = queue.current.then(async () => {
      if (!item.file || !patientNumber) return
      patch(item.key, { status: 'uploading', progress: 0, error: undefined })
      try {
        const r = await documentService.uploadStaged(item.file, patientNumber, item.documentType, percent => patch(item.key, { progress: percent }))
        const stored: StepUpload = {
          id: r.id, storageKey: r.storageKey, filename: r.filename, name: r.originalName || item.name, size: r.sizeBytes ?? item.size,
          documentType: r.documentType || item.documentType, at: r.createdAt, sent: false,
        }
        setItems(prev => prev.filter(i => i.key !== item.key))
        setStepUploads(prev => [...prev, stored])
      } catch (err) {
        logger.warn('[Step5] upload failed:', getApiErrorCode(err) ?? getApiErrorStatus(err))
        reportUploadError(err, { patientNumber, step: 'step5', filename: item.name })
        patch(item.key, { status: 'error', error: uploadError(err) })
      }
    })
  }, [patientNumber, setStepUploads])

  const addFiles = (files: FileList | File[] | null) => {
    if (!files || !docType) return
    const fresh: Item[] = []
    for (const file of Array.from(files)) {
      const item: Item = { key: `c-${nextKey++}`, name: file.name, size: file.size, documentType: docType, status: isTestMode ? 'local' : 'queued', progress: 0, file }
      // Refuse locally what the server would refuse anyway, before the patient waits for the transfer
      if (/\.(heic|heif)$/i.test(file.name) || /^image\/(heic|heif)$/i.test(file.type)) { item.status = 'error'; item.error = 'heic'; item.file = undefined }
      else if (!ALLOWED_TYPES.has(file.type)) { item.status = 'error'; item.error = 'unsupported'; item.file = undefined }
      else if (file.size > MAX_UPLOAD_BYTES) { item.status = 'error'; item.error = 'tooLarge'; item.file = undefined }
      // no patient number yet (should not happen on this step): nothing to file it under
      else if (!isTestMode && !patientNumber) { item.status = 'error'; item.error = 'failed'; item.file = undefined }
      fresh.push(item)
    }
    setItems(prev => [...prev, ...fresh])
    for (const item of fresh) if (item.status === 'queued') upload(item)
  }

  /** Opens the file picker (or the camera) */
  const pick = (camera: boolean) => { (camera ? cameraInput : fileInput).current?.click() }
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => { addFiles(e.target.files); e.target.value = '' }
  const retry = (item: Item) => { if (item.file) upload(item) }
  const dismiss = (key: string) => setItems(prev => prev.filter(i => i.key !== key))

  /** Take back a stored file that was not sent yet */
  const removeStored = async (u: StepUpload) => {
    if (!patientNumber) return
    try {
      await documentService.removeStaged(patientNumber, u.id)
      setStepUploads(prev => prev.filter(x => x.id !== u.id))
    } catch (err) {
      // already sent by the automatic send, or the connection dropped: say so, keep it listed
      logger.warn('[Step5] remove failed:', getApiErrorCode(err) ?? getApiErrorStatus(err))
      toast.error(t('uploadLink.removeFailed'))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Test mode never writes to the document buckets — the selected files are dropped, not uploaded
    if (isTestMode) {
      if (items.some(i => i.status === 'local')) toast.success(t('common.testModeUploadSkipped'))
      setItems([])
      onNext()
      return
    }

    const waiting = stepUploads.filter(u => !u.sent)
    if (waiting.length > 0 && patientNumber) {
      setFinalizing(true)
      try {
        const r = await documentService.finalizeUploads(patientNumber, waiting.map(u => u.id))
        const failed = new Set(r.failedTypes)
        // These files' own entries (left by an earlier failed send) give way to the documents they went into
        const covered = new Set(waiting.filter(u => !failed.has(u.documentType)).map(u => u.storageKey))
        const rest = uploadedDocuments.filter(d => !covered.has(d.id))
        const known = new Set(rest.map(d => d.id))
        setUploadedDocuments([...rest, ...r.documents.filter(d => !known.has(d.id))])
        setStepUploads(prev => prev.map(u => (waiting.some(w => w.id === u.id) && !failed.has(u.documentType) ? { ...u, sent: true } : u)))
        if (failed.size > 0) logger.warn('[Step5] some document types were not sent yet:', [...failed].join(','))
      } catch (err) {
        // Never blocks the questionnaire: the files are stored and the server sends them within 24 h.
        // Until then the webhook gets the single files, as before.
        logger.error('[Step5] sending the documents failed:', err)
        reportUploadError(err, { patientNumber, step: 'step5', filename: 'finalize' })
        const known = new Set(uploadedDocuments.map(d => d.id))
        setUploadedDocuments([...uploadedDocuments, ...waiting.filter(u => !known.has(u.storageKey)).map(u => ({ id: u.storageKey, filename: u.filename, documentType: u.documentType }))])
      } finally {
        setFinalizing(false)
      }
    }
    // Refused files the patient left in the list are simply not part of it
    setItems(prev => prev.filter(i => i.status !== 'error'))
    onNext()
  }

  const busy = items.some(i => i.status === 'uploading' || i.status === 'queued')
  const pendingCount = stepUploads.filter(u => !u.sent).length
  // In lists the short name is enough — the examples in brackets are for choosing
  const typeLabel = (type?: string | null) => (isDocumentType(type) ? t(`documentTypes.${type}`).replace(/\s*\([^)]*\)\s*$/, '') : null)
  const formatTime = (iso: string) => new Date(iso).toLocaleString(lang, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

  const storedRow = (u: StepUpload) => (
    <li key={u.id} className="px-3 py-2.5 flex items-start gap-3 bg-white" data-status={u.sent ? 'sent' : 'pending'}>
      <span className="mt-0.5 flex-shrink-0">{u.sent ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-amber-600" />}</span>
      <div className="min-w-0 flex-1">
        <p className="text-body text-foreground truncate" title={u.name}>{u.name}</p>
        <p className="text-small text-muted-foreground">
          {[typeLabel(u.documentType), formatFileSize(u.size, lang), formatTime(u.at)].filter(Boolean).join(' · ')}
          {' · '}<span className={u.sent ? 'text-green-700' : 'text-amber-700'}>{u.sent ? t('step5.sentFile') : t('step5.pendingFile')}</span>
        </p>
      </div>
      {!u.sent && (
        <button type="button" onClick={() => void removeStored(u)} disabled={finalizing} className="p-1.5 rounded hover:bg-muted text-muted-foreground flex-shrink-0" title={t('uploadLink.removeFile')} aria-label={t('uploadLink.removeFile')}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </li>
  )

  const itemRow = (item: Item) => (
    <li key={item.key} className="px-3 py-2.5 flex items-start gap-3 bg-white" data-status={item.status}>
      <span className="mt-0.5 flex-shrink-0">
        {item.status === 'local' && <FileText className="w-5 h-5 text-primary" />}
        {(item.status === 'uploading' || item.status === 'queued') && <Loader2 className="w-5 h-5 text-primary animate-spin" />}
        {item.status === 'error' && <AlertTriangle className="w-5 h-5 text-destructive" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-body text-foreground truncate" title={item.name}>{item.name}</p>
        {item.status === 'local' && <p className="text-small text-muted-foreground">{[typeLabel(item.documentType), formatFileSize(item.size, lang)].filter(Boolean).join(' · ')}</p>}
        {(item.status === 'uploading' || item.status === 'queued') && (
          <>
            <p className="text-small text-muted-foreground">{t('uploadLink.uploading')} {item.status === 'uploading' ? `${item.progress}%` : ''}</p>
            <div className="mt-1 h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${item.progress}%` }} /></div>
          </>
        )}
        {item.status === 'error' && item.error && (
          <p className="text-small text-destructive">{t(`uploadLink.error.${item.error}`, { size: formatFileSize(item.size, lang), max: formatFileSize(MAX_UPLOAD_BYTES, lang) })}</p>
        )}
      </div>
      {(item.status === 'error' || item.status === 'local') && (
        <span className="flex items-center gap-1 flex-shrink-0">
          {item.status === 'error' && item.file && (
            <button type="button" onClick={() => retry(item)} className="p-1.5 rounded hover:bg-primary/10 text-primary" title={t('uploadLink.retry')} aria-label={t('uploadLink.retry')}>
              <RotateCw className="w-4 h-4" />
            </button>
          )}
          <button type="button" onClick={() => dismiss(item.key)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title={t('uploadLink.remove')} aria-label={t('uploadLink.remove')}>
            <X className="w-4 h-4" />
          </button>
        </span>
      )}
    </li>
  )

  const pickButton = (camera: boolean) => (
    <Button
      type="button"
      variant={camera ? 'secondary' : undefined}
      disabled={!docType}
      onClick={() => pick(camera)}
      className="w-full sm:w-auto inline-flex items-center justify-center gap-2"
    >
      {camera ? <Camera className="w-4 h-4" /> : <FileText className="w-4 h-4" />} {camera ? t('uploadLink.takePhoto') : t('uploadLink.chooseFile')}
    </Button>
  )

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <h2 className="text-h2 text-foreground mb-2">
          {t('step5.title')}
        </h2>
        <p className="text-body text-foreground mb-2">
          {personalData.firstName} {personalData.lastName}
        </p>
        <p className="text-body text-muted-foreground mb-8">
          {t('step5.subtitle')}
        </p>

        <label className="block mb-3">
          <span className="block text-body font-medium text-foreground mb-1.5">{t('uploadLink.documentTypeLabel')}</span>
          <select
            value={docType}
            onChange={e => setDocType(e.target.value)}
            className={cn('w-full px-3 py-2.5 border rounded-input text-body bg-white', docType ? 'border-border text-foreground' : 'border-primary text-muted-foreground')}
            data-testid="step-upload-document-type"
          >
            {!docType && <option value="" disabled>{t('uploadLink.documentTypePlaceholder')}</option>}
            {CHOOSABLE_DOCUMENT_TYPES.map(type => <option key={type} value={type} className="text-foreground">{t(`documentTypes.${type}`)}</option>)}
          </select>
        </label>
        <div
          onDragOver={e => { e.preventDefault(); if (docType) setDragging(true) }}
          onDragLeave={e => { e.preventDefault(); setDragging(false) }}
          onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files) }}
          className={cn('mb-2 rounded-card border-2 border-dashed px-4 py-8 text-center transition-colors', dragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/30')}
        >
          <UploadCloud className="w-10 h-10 text-primary mx-auto mb-3" />
          {!isMobile && (
            <p className="text-body text-foreground mb-3">
              {t('uploadLink.dropTitle')} <span className="text-muted-foreground">{t('uploadLink.dropOr')}</span>
            </p>
          )}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
            {pickButton(false)}
            {isMobile && pickButton(true)}
          </div>
          {!docType && <p className="text-small text-muted-foreground mt-3">{t('uploadLink.documentTypeFirst')}</p>}
        </div>
        <p className="text-small text-muted-foreground text-center mb-6">{t('uploadLink.formats', { max: formatFileSize(MAX_UPLOAD_BYTES, lang) })}</p>

        <input ref={fileInput} type="file" accept={ACCEPT} multiple onChange={onPick} className="hidden" data-testid="step-upload-input" />
        <input ref={cameraInput} type="file" accept="image/jpeg,image/png" capture="environment" onChange={onPick} className="hidden" />

        {(stepUploads.length > 0 || items.length > 0) && (
          <>
            <h3 className="text-body font-medium text-foreground mb-2">{t('step5.uploadedTitle')}</h3>
            <ul className="mb-3 divide-y divide-border border border-border rounded-card overflow-hidden" aria-live="polite" data-testid="step-upload-list">
              {stepUploads.map(storedRow)}
              {items.map(itemRow)}
            </ul>
            {pendingCount > 0 && <p className="text-small text-muted-foreground mb-2">{t('step5.sendHint')}</p>}
          </>
        )}

        <FormNavigation
          onBack={onBack}
          nextDisabled={busy || finalizing}
          nextLabel={busy ? t('common.uploading') : finalizing ? t('common.sending') : t('common.next')}
        />
      </form>
    </div>
  )
}
